<template>
  <div class="ui-full-calendar-wrapper">
    <div class="ui-full-calendar-options">
      <slot name="before-options"/>
      <div class="ui-full-calendar-options-control">
        <div class="ui-full-calendar-options-control__list">
          <ui-switcher
            class="ui-full-calendar-options-control__tab-list"
            :value="innerType"
            @input="setType"
          >
            <ui-switcher-item
              class="ui-full-calendar-options-control__tab-item"
              v-for="item in initCalendarTypes"
              :key="item.name"
              :active-id="innerType"
              :title="item.name"
              :id="item.type"
            />
          </ui-switcher>
          <div class="ui-full-calendar__counter _desktop">
            <span v-skeleton="{show: loading, width: 124}">
              {{counterText}} {{eventsCounter}}
            </span>
          </div>
        </div>
        <div class="ui-full-calendar-options-actions">
          <div
            class="ui-full-calendar-options-actions__list"
            v-if="hasExports || hasImports"
          >
            <ui-button-select
              v-if="hasExports"
              :list="exports"
              icon-name="uiExport"
            />
            <ui-button-select
              v-if="hasImports"
              :list="imports"
              icon-name="uiImport"
            />
          </div>
          <ui-button
            class="ui-full-calendar-options-actions__button"
            v-if="canCreate"
            :title="createActionTitle"
            icon="uiPlus"
            type="secondary"
            button-style="outline"
            @click="$emit('create')"
          />
        </div>
      </div>
    </div>
    <slot name="after-options"/>
    <div class="ui-full-calendar__counter _mobile">
      <span v-skeleton="{show: loading, width: 124}">
        {{counterText}} {{eventsCounter}}
      </span>
    </div>
    <v-calendar
        ref="calendar"
        :type="innerType"
        :value="value"
        :weekdays="weekday"
        :events="events"
        :weekday-format="monthFormat"
        :short-intervals="false"
        :interval-height="intervalHeight"
        :interval-width="90"
        :event-overlap-threshold="60"
        :first-interval="firstTime"
        :interval-minutes="60"
        :interval-count= "intervalCount"
        event-start="start"
        :event-color="getEventColor"
        :event-overlap-mode="eventOverlapMode"
        :show-month-on-first="false"
        :event-more="hasEventMore"
        event-more-text="+{0}"
        locale="ru"
        class="ui-full-calendar"
        :class="calendarClass"
        @input="setValue"
        @change="getRange"
        @click:date="viewDay"
        @click:day="clickDay"
        @click:day-category="viewDay"
        @click:interval="clickInterval"
        @click:event="clickEvent"
        @click:time="clickTime"
        @click:more="viewDay"
        @mouseleave:event="eventMouseleave"
        @mousemove:event="eventMousemove"
        @mousedown:day="startSelectRangeDates"
        @mouseup:day="finishSelectRangeDates"
        @mousemove:day="addSelectRangeDate"
    >
      <template #day-label-header="data">
        <div @click="viewDay(data)" class="ui-full-calendar-day-label-header">
          <div class="ui-full-calendar-day-label-header__day">
            {{data.day}}
          </div>
          <div class="ui-full-calendar-day-label-header__weekday">
            {{monthFormat(data)}}
          </div>
        </div>
      </template>
      <template #day="{date}">
         <div v-if="isSelectedRangeDate(date)" class="active-day" :key="`active-${date}`"/>
      </template>
      <template #day-body="{ date, week }">
        <div v-if="!isTypeMonth" class="ui-full-calendar-current-time" :key="date">
          <div
              v-if="isFirstDate(date, week[0].date)"
              id="ui-full-calendar-current-time__before"></div>
          <div
              class="ui-full-calendar-current-time__cell"
              :class="{ first: isFirstDate(date, week[0].date)}"
          ></div>
        </div>
      </template>
      <template #event="{ event }">
        <slot name="event" :event="event">
          <ui-full-calendar-event
              :event="event"
              :type="innerType"
              @cancel-task="cancelTask"
              @success-task="successTask"
          />
        </slot>
      </template>
    </v-calendar>
    <slot />
  </div>
</template>

<script>
import VCalendar from 'vuetify/lib/components/VCalendar/VCalendar'
import { firstUpper } from '../../libs/helpTools'
import UiSwitcher from '../switcher/UiSwitcher.vue'
import UiSwitcherItem from '../switcher/UiSwitcherItem.vue'
import { TYPE_DAY, TYPE_MONTH, TYPE_WEEK } from './calendarConst.js'
import UiFullCalendarEvent from './UiFullCalendarEvent.vue'
import UiButtonSelect from '../UiButtonSelect.vue'
import UiButton from '../button/UiButton.vue'
import { date as dayjs } from '../../plugins/date'

import Vue from 'vue'
import { dateFormat } from '../../libs/date'

Vue.prototype.$vuetify = {
  rtl: false,
  lang: {
    t: (value, params) => value.replace(/\{(\d+)\}/g, params)
  }
}

export default {
  name: 'UiFullCalendar',
  props: {
    value: {
      type: String,
      default: ''
    },

    type: {
      type: String,
      default: TYPE_MONTH
    },

    events: {
      type: Array,
      default: () => []
    },

    intervalCount: {
      type: Number,
      default: 17
    },

    firstTime: {
      type: String,
      default: '06:00'
    },

    eventOverlapMode: {
      type: String,
      default: 'stack'
    },

    loading: {
      type: Boolean,
      default: false
    },

    isOpenDay: {
      type: Boolean,
      default: true
    },

    hasEventMore: {
      type: Boolean,
      default: true
    },

    initCalendarTypes: {
      type: Array,
      default: () => [
        {
          type: TYPE_DAY,
          name: 'День'
        }, {
          type: TYPE_WEEK,
          name: 'Неделя'
        }, {
          type: TYPE_MONTH,
          name: 'Месяц'
        }
      ]
    },

    hasSelectRangeDates: {
      type: Boolean,
      default: false
    },

    selectedRangeDates: {
      type: Object,
      default: () => ({
        start: null,
        end: null
      })
    },

    exports: Array,
    imports: Array,
    canCreate: {
      type: Boolean,
      default: true
    },
    createActionTitle: {
      type: String,
      default: 'Добавить задачу'
    },
    counterText: {
      type: String,
      default: 'Найдено задач:'
    }
  },
  components: {
    UiFullCalendarEvent,
    UiSwitcherItem,
    UiSwitcher,
    VCalendar,
    UiButtonSelect,
    UiButton
  },

  data () {
    return {
      innerType: this.type,
      weekday: [1, 2, 3, 4, 5, 6, 0],
      isCalendarReady: false,
      nowTimer: null,
      isDateSelect: false,
      dateBase: null
    }
  },

  computed: {
    innerSelectedRangeDates: {
      get () {
        return this.selectedRangeDates
      },

      set (value) {
        this.$emit('update:selected-range-dates', value)
      }
    },

    calendar () {
      return this.isCalendarReady ? this.$refs.calendar : {}
    },

    timesNow () {
      return this.calendar.times?.now
    },

    calendarClass () {
      return {
        [this.innerType]: true
      }
    },

    isTypeDay () {
      return this.innerType === TYPE_DAY
    },

    isTypeMonth () {
      return this.innerType === TYPE_MONTH
    },

    intervalHeight () {
      return this.isTypeDay ? 146 : 76
    },
    hasExports () {
      return Boolean(this.exports?.length)
    },
    hasImports () {
      return Boolean(this.imports?.length)
    },
    eventsCounter () {
      return this.events.length
    }
  },

  mounted () {
    this.isCalendarReady = true
    this.setTimer()
  },

  methods: {
    setValue (value) {
      this.$emit('input', value)
    },

    isFirstDate (date, weekDate) {
      return date === weekDate
    },

    viewDay (data) {
      if (!this.isOpenDay) return
      this.setValue(data.date)
      this.setType(TYPE_DAY)
      this.$emit('click:date', data)
    },

    clickInterval (date) {
      this.$emit('click:interval', date)
    },

    clickDay (date) {
      if (this.isTypeDay) return
      this.$emit('click:interval', date)
    },

    clickTime (date) {
      this.$emit('click:time', date)
    },

    cancelTask (data) {
      this.$emit('cancel-task', data)
    },

    successTask (data) {
      this.$emit('success-task', data)
    },

    setType (type) {
      if (type === this.innerType) return
      this.innerType = type
      if (this.isTypeMonth) this.clearUpdateTimer()
      this.setTimer()
      this.$emit('update:type', this.innerType)
    },

    getRange (dates) {
      this.$emit('change-range-dates', dates)
    },

    getEventColor (event) {
      return event.color
    },

    monthFormat ({ date }) {
      return firstUpper(dateFormat(date, 'dddd', false))
    },

    next () {
      this.calendar.next()
    },

    prev () {
      this.calendar.prev()
    },

    setTimer () {
      if (this.isTypeMonth) return
      this.clearUpdateTimer()
      this.updateTime()
      this.nowTimer = setInterval(() => {
        this.updateTime()
      }, 60 * 1000)
    },

    updateTime () {
      setTimeout(() => {
        this.calendar.updateTimes?.()
        this.setNowY()
      }, 0)
    },

    clearUpdateTimer () {
      this.nowTimer?.clearInterval?.()
    },

    setNowY () {
      const elements = document.getElementsByClassName('ui-full-calendar-current-time')
      const time = document.getElementById('ui-full-calendar-current-time__before')

      if (!elements || !time) return
      const nowY = this.calendar.timeToY(this.timesNow)
      Array.from(elements).forEach((item, i) => {
        elements[i].style.top = `${nowY}px`
      })
      document.getElementById('ui-full-calendar-current-time__before').innerText = this.timesNow?.time
    },

    eventMouseleave (event) {
      this.$emit('mouseleave:event', event)
    },

    eventMousemove (event) {
      this.$emit('mousemove:event', event)
    },

    clickEvent (event) {
      this.$emit('click:event', event)
    },

    startSelectRangeDates ({ date }) {
      if (!this.hasSelectRangeDates) return
      this.clearSelectedRangeDates()
      this.innerSelectedRangeDates = {
        start: date,
        end: date
      }
      this.baseRangeDate = date
      this.isDateSelect = true
    },

    finishSelectRangeDates () {
      if (!this.isDateSelect || !this.hasSelectRangeDates) return
      this.isDateSelect = false
      if (this.innerSelectedRangeDates.start === this.innerSelectedRangeDates.end) return
      this.$emit('select-range-dates', this.innerSelectedRangeDates)
    },

    addSelectRangeDate ({ date }) {
      if (!this.isDateSelect || !this.hasSelectRangeDates) return
      if (this.isEndRangeDate(date)) {
        this.innerSelectedRangeDates = {
          start: this.baseRangeDate,
          end: date
        }
      }
      if (this.isStartRangeDate(date)) {
        this.innerSelectedRangeDates = {
          start: date,
          end: this.baseRangeDate
        }
      }
    },

    isEndRangeDate (date) {
      return dayjs(date).isSameOrAfter(this.innerSelectedRangeDates.end) || dayjs(date).isSameOrAfter(this.innerSelectedRangeDates.start)
    },

    isStartRangeDate (date) {
      return dayjs(date).isSameOrBefore(this.innerSelectedRangeDates.start)
    },

    isSelectedRangeDate (date) {
      if (!this.innerSelectedRangeDates.start || !this.innerSelectedRangeDates.end) return
      return dayjs(date).isBetween(this.innerSelectedRangeDates.start, this.innerSelectedRangeDates.end, 'day', '[]')
    },

    clearSelectedRangeDates () {
      this.innerSelectedRangeDates = {
        start: null,
        end: null
      }
      this.baseRangeDate = null
    }
  },

  beforeDestroy () {
    this.clearUpdateTimer()
  },

  watch: {
    events: {
      handler () {
        this.setTimer()
      },
      deep: true
    }
  }
}
</script>
<style lang="scss" scoped>
@mixin activeDateBtn {
  font-weight: 500;
  color: $white;
  background-color: $blue-2 !important;
  border-radius: $border-radius-01;
}

@mixin activeDay {
  box-shadow: inset 0 0 0 1px #bbd8ff;
  background-color: rgba(213, 232, 255, 0.09);
}

.active-day {
  @include activeDay;

  z-index: -1;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.ui-full-calendar {

  &-options {
    display: grid;
    gap: 12px 16px;
    margin-bottom: 16px;

    &-control {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 12px 24px;

      &__list {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px 24px;
      }
    }

    &-actions {
      display: flex;
      justify-content: space-between;
      gap: 12px 8px;

      &__list {
        display: flex;
        gap: 12px 8px;
      }
    }
  }

  &__counter {
    @include font-subtitle();
    color: $gray-4;

    &._mobile {
      margin-bottom: 16px;

      @include media-min-md {
        display: none;
      }
    }

    &._desktop {
      @include media-max-md {
        display: none;
      }
    }
  }
}

.ui-full-calendar-loading {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.ui-full-calendar-day-label-header {
  @include flex-center;
  flex-direction: column;
  height: 100%;
  width: 100%;

  &__day {
    @include font-title-24;
  }

  &__weekday {
    @include font-16;
  }
}

.ui-full-calendar-wrapper ::v-deep(.ui-full-calendar) {
  @include hidden-scrollbar;
  overflow: scroll;

  .v-calendar-weekly__head {
    display: flex;
    background-color: $gray-7;
    border-top-left-radius: $border-radius-00;
    border-top-right-radius: $border-radius-00;
  }

  .v-calendar-daily_head-weekday {
    display: none;
  }

  .v-calendar-daily_head-day-label {
    height: 100%;
    width: 100%;
  }

  .v-calendar-weekly__week {
    display: flex;
    flex: 1;
    height: unset;
    min-height: 0;

    & .v-calendar-weekly__day {
      padding: 1px;

      &:first-child {
        border-left: $gray-5 1px solid;
      }

      .v-btn {
        @include flex-center;

        width: 24px;
        height: 24px;
        margin: 7px 6px 0 0;
        color: $primary;
        cursor: pointer;

        &:hover {
          @include activeDateBtn;

          span {
            color: $white;
          }
        }

        &:focus {
          outline: 0;
        }
      }

      &.v-outside .v-btn {
        color: $gray-4;
      }

      &.v-present .v-btn {
        @include activeDateBtn;
      }
    }
  }

  .v-calendar-weekly__day-label {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 4px;

    button {
      @include font-16;
      border: 0;
      background: transparent;
    }
  }

  .v-calendar-weekly__head-weekday {
    @include font-16;
    @include flex-center;

    flex: 1;
    width: 124px;
    min-width: 124px;
    height: 50px;
    font-weight: 500;
    color: $gray-2;
    background-color: $gray-7;
  }

  .v-calendar-weekly__day {
    position: relative;
    flex: 1;
    width: 0;
    min-width: 124px;
    height: 148px;
    overflow: visible;
    user-select: none;
    border-right: $gray-5 1px solid;
    border-bottom: $gray-5 1px solid;

    &:hover {
      @include activeDay;
    }
  }

  &.v-calendar-daily {
    .v-calendar-daily__scroll-area {
      flex: 1 1 auto;
      display: flex;
      align-items: flex-start;
    }

    .v-calendar-daily__intervals-body {
      min-width: 90px;
      border-right: $gray-5 1px solid;
    }

    .v-calendar-daily__day-container {
      display: flex;
      flex: 1;
      width: 100%;
      height: 100%;
      border: $gray-5 1px solid;
      border-top: initial;
      border-right: initial;
    }

    .v-calendar-daily__day {
      flex: 1;
      width: 0;
      min-width: 110px;
      position: relative;
      border-right: $gray-5 1px solid;

      .v-calendar-daily__day-interval {
        min-width: 110px;

        &:first-child {
          border-top: initial;
        }
      }
    }

    .v-calendar-daily__intervals-head {
      min-width: 90px;
      border-top-left-radius: $border-radius-00;
      background-color: $gray-7;
    }

    .v-calendar-daily__head {
      display: flex;
      height: 90px;
      border-top-right-radius: $border-radius-00;
      border-top-left-radius: $border-radius-00;
      border-bottom: 1px solid $gray-5;
      background-color: $gray-7;
    }

    .v-calendar-daily_head-day {
      @include font-16;
      @include flex-center;

      display: flex;
      flex-direction: column-reverse;
      flex: 1;
      min-width: 110px;
      background-color: $gray-7;
      color: $gray-2;
      cursor: pointer;

      .v-btn {
        @include getFontTitleSize(24);

        color: inherit;
        border: initial;
        background-color: transparent;
        cursor: pointer;
      }

      &.v-present {
        color: $white;
        background-color: $primary-2;
        border-radius: $border-radius-00;
      }

      &:last-child {
        border-top-right-radius: $border-radius-00;
      }
    }

    .v-calendar-daily__day-interval {
      color: $gray-5;
      border-top: $gray-5 1px solid;
    }

    .v-calendar-daily__pane {
      flex: 1 1 auto;
      display: flex;
      align-items: flex-start;
    }

    .v-calendar-daily__interval {
      @include font-14;
      text-align: center;
      color: $gray-2;
    }

    .v-calendar-daily__interval-text {
      position: relative;
      top: -9px;
    }
  }

  &.day {
    .ui-full-calendar-day-label-header {
      flex-direction: row;
      justify-content: flex-start;
      border-top-left-radius: $border-radius-00;

      &__day {
        @include getFontTitleSize(44);
        margin: 0 14px 0 24px;
      }

      &__weekday {
        @include getFontSize(24);
      }
    }

    .v-calendar-daily__head {
      margin-right: 0 !important;
      border: 0;
    }

    .v-calendar-daily__intervals-head {
      width: 0 !important;
      min-width: 0;
    }

    .v-calendar-daily_head-day.v-present {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    .v-event-timed {
      min-height: 86px;
    }
  }

  .v-event {
    height: initial !important;;
    margin: 0 !important;
  }

  .v-event-timed {
    @include font-12;
    @include ellipsis;

    position: absolute;
    padding: 2px;
    cursor: pointer;
    border-radius: $border-radius-01;
    pointer-events: all;
  }

  .v-event-timed-container {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: none;
  }

  .v-event-more {
    @include flex-center;
    @include font-12;

    width: max-content;
    min-width: 25px;
    height: 24px !important;
    margin: 9px 6px 0 auto;
    padding-left: 0;
    font-weight: 500;
    color: $blue-1;
    background-color: $blue-4;
    border-radius: $border-radius-01;
    cursor: pointer;
  }

  .ui-full-calendar-current-time {
    position: absolute;
    width: 100%;
  }

  .ui-full-calendar-current-time__cell {
    position: absolute;
    left: -1px;
    right: 0;
    height: 4px;
    background-color: $red-3;
    pointer-events: none;
  }

  #ui-full-calendar-current-time__before {
    @include flex-center;
    @include font-12;

    position: absolute;
    z-index: 1;
    width: 44px;
    height: 16px;
    border-radius: 100px;
    margin-top: -6px;
    margin-left: -22px;
    color: $white;
    background-color: $red-2;
    white-space: nowrap;
  }
}

@include media-max-wide-sm {
  .ui-full-calendar-options {
    margin-bottom: 12px;

    &-control {
      flex-direction: column;
      align-items: unset;

      &__list {
        flex-direction: column;
        align-items: unset;
        width: 100%;
      }

      &__tab-list {
        width: 100%
      }

      &__tab-item {
        flex-grow: 1;
      }
    }

    &-actions {
      width: 100%;
      justify-content: unset;

      ::v-deep() &__button {
        width: 100%;
      }
    }
  }
}
</style>
